/**
 * FailedProductCard.tsx
 *
 * Outbound-bubble UI for a TikTok PRODUCT_CARD send that the server
 * marked as `status: 'failed'`. Spec: `docs/prd/tiktok-chat/features/
 * product-card-presentation.v2.html` slide 22 ("Fail fast. One envelope.").
 *
 * Rules from the PRD:
 *  - No product image, title, or price — we don't render data we don't
 *    have. Avoids the buyer seeing stale or wrong info.
 *  - Diagonal-stripe placeholder where the image would go — visually
 *    obvious it's a broken card, not a styled choice.
 *  - Raw product id shown in monospace so the seller can copy it for
 *    support.
 *  - Reason text is the human-friendly mapping of the server error code
 *    (see `failedProductReason.ts`), not the raw code.
 *  - No retry / remove buttons inside the card — `ChatFailedMenu`
 *    adjacent to the bubble owns the retry interaction.
 */

import { FC } from 'react'
import { mapFailedProductReason } from './failedProductReason'

export interface FailedProductCardProps {
  productId: string
  errorCode?: string
  rawErrorMessage?: string
}

const FailedProductCard: FC<FailedProductCardProps> = ({
  productId,
  errorCode,
  rawErrorMessage,
}) => {
  const reason = mapFailedProductReason(errorCode, rawErrorMessage)

  return (
    <div
      data-testid={`failed-product-card-${productId}`}
      className="tw-bg-white tw-border tw-border-[#f87171] tw-rounded-[14px] tw-overflow-hidden tw-w-[280px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.3)]"
    >
      {/* Diagonal-stripe placeholder where the product image would go. */}
      <div
        className="tw-flex tw-items-center tw-justify-center tw-text-gray-400"
        style={{
          height: 100,
          fontSize: 28,
          backgroundImage:
            'repeating-linear-gradient(45deg,#f0f0f0,#f0f0f0 10px,#e5e5e5 10px,#e5e5e5 20px)',
        }}
        aria-hidden="true"
        data-testid="failed-product-card-placeholder"
      >
        📦
      </div>
      <div className="tw-px-[14px] tw-py-3">
        <div
          className="tw-text-[#f87171] tw-font-bold tw-uppercase tw-mb-1"
          style={{ fontSize: 10, letterSpacing: '0.06em' }}
          data-testid="failed-product-card-label"
        >
          ⚠ Product unavailable
        </div>
        <div
          className="tw-bg-gray-100 tw-rounded tw-px-2 tw-py-1 tw-mb-2 tw-text-gray-800 tw-break-all"
          style={{
            fontFamily:
              "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
            fontSize: 12,
          }}
          data-testid="failed-product-card-id"
        >
          {productId}
        </div>
        <div
          className="tw-text-gray-500 tw-leading-snug"
          style={{ fontSize: 12 }}
          data-testid="failed-product-card-reason"
        >
          {reason}
        </div>
      </div>
    </div>
  )
}

export default FailedProductCard
